import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface NeuralNetworkAnimationProps {
  glowColor: string;
}

export const NeuralNetworkAnimation = ({ glowColor }: NeuralNetworkAnimationProps) => {
  const pointsRef = useRef<THREE.Points>(null);

  // Create particles
  const particleCount = 800; // Reduced particle count
  const particles = new Float32Array(particleCount * 3);
  const velocities = new Float32Array(particleCount * 3);
  const phases = new Float32Array(particleCount);

  for (let i = 0; i < particles.length; i += 3) {
    particles[i] = (Math.random() - 0.5) * 8;     // x
    particles[i + 1] = (Math.random() - 0.5) * 8; // y
    particles[i + 2] = (Math.random() - 0.5) * 8; // z

    // Much slower velocities
    velocities[i] = (Math.random() - 0.5) * 0.002;
    velocities[i + 1] = (Math.random() - 0.5) * 0.002;
    velocities[i + 2] = (Math.random() - 0.5) * 0.002;

    // Random phase for sinusoidal movement
    phases[i / 3] = Math.random() * Math.PI * 2;
  }

  useFrame((state) => {
    if (pointsRef.current) {
      const positions = pointsRef.current.geometry.attributes.position.array as Float32Array;
      const time = state.clock.getElapsedTime() * 0.1; // Slower time scale
      
      for (let i = 0; i < positions.length; i += 3) {
        const particleIndex = i / 3;
        
        // Very gentle sinusoidal movement
        const sinOffset = Math.sin(time * 0.1 + phases[particleIndex]) * 0.005;
        const cosOffset = Math.cos(time * 0.08 + phases[particleIndex]) * 0.005;

        // Update positions with very slow velocities
        positions[i] += velocities[i] + sinOffset;
        positions[i + 1] += velocities[i + 1] + cosOffset;
        positions[i + 2] += velocities[i + 2] + sinOffset;

        // Gentle boundary check
        for (let j = 0; j < 3; j++) {
          const pos = positions[i + j];
          const boundary = 4;
          
          if (Math.abs(pos) > boundary) {
            const direction = pos > 0 ? -1 : 1;
            positions[i + j] = boundary * direction;
            velocities[i + j] *= -0.5; // Gentler bounce
            velocities[i + j] += (Math.random() - 0.5) * 0.001; // Tiny random adjustment
          }
        }
      }

      pointsRef.current.geometry.attributes.position.needsUpdate = true;
      
      // Extremely slow rotation
      pointsRef.current.rotation.x += 0.0001;
      pointsRef.current.rotation.y += 0.00015;
      pointsRef.current.rotation.z += 0.00005;
    }
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={particleCount}
          array={particles}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.1}
        color={glowColor}
        transparent
        opacity={0.3}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </points>
  );
};